import { z } from 'zod';

// Governed vocabulary for the escalations domain — the durable record of a
// safety signal raised about a member.
//
// The `as const` array drives BOTH the `text`+CHECK constraint rendered in the
// DB schema and the wire/contract schema here, so the DB and the validators
// cannot drift (ADR-056 Rule 8; mirrors member-profiles/schema.ts).

// ─── escalation source ──────────────────────────────────────────────────────
//
// Deliberately named by SOURCE, not by clinical category, because that is the
// only thing either producer can state with confidence.
//
// `assessment_suicide_screen_positive` — the adaptive PHQ/GAD instrument scored
// `result_key = 'suicide_risk'`. That verdict comes from PHQ-9 item 9 or the
// standalone session screen, both of which ask a SINGLE conflated question
// ("thoughts that you would be better off dead, or of hurting yourself in some
// way") over a two-week window, so suicidal ideation and self-harm are not
// separable from it, and acuity is not determinable at all (the acute vs
// non-acute line is drawn off ASQ item 5, which the product does not
// administer).
//
// `agent_crisis_flag` — the conversational agent called `escalate_crisis`. The
// tool takes only a free-text reason and carries no category, while the agent
// prompt fires it across suicidal ideation, self-harm, intent to harm others,
// and acute medical risk (withdrawal symptoms, seizures, blackouts, overdose
// concern, driving impaired). So the call means "the agent judged this a
// crisis" and nothing more machine-readable than that.
//
// `transcript_crisis_scan` — the post-call summariser judged the finished
// conversation a crisis and the agent had not flagged it during the call. The
// verdict is one boolean over the whole transcript ("acute distress, self-harm
// risk, or crisis content"), produced for the summary rather than as an alert
// trigger, so it carries no category, no severity and no reason. It means "a
// second reading of the transcript saw a crisis the call did not act on".
//
// `intake_withdrawal_screen_positive` — a member on the manual onboarding path
// reported a withdrawal symptom the structured check treats as a safety flag.
// Which answers raise is per program: the main program's
// `withdrawal_escalation_min_risk` (absent → `positive`) decides whether a
// positive answer raises, and an urgent answer always does. No conversation
// exists; the evidence is the withdrawal check row on the intake panel.
//
// A clinical category vocabulary is a separate, clinically-owned decision; the
// `escalations.category` column exists for it and stays null until then. Do not
// infer a category from any source.
export const ESCALATION_SOURCE = [
  'assessment_suicide_screen_positive',
  'agent_crisis_flag',
  'transcript_crisis_scan',
  'intake_withdrawal_screen_positive',
] as const;
export const escalationSourceSchema = z.enum(ESCALATION_SOURCE);
export type EscalationSource = z.infer<typeof escalationSourceSchema>;

// The sources whose signal is a conversation. At most one of them should ever
// alert the care team about a given conversation. That is enforced in ONE
// direction only: the post-call scan checks for an in-call flag (and for its
// own earlier escalation) before raising. The in-call producer checks nothing
// — it does not need to, because it records its flags when the transcript is
// first projected, which is before the event that starts the scan is
// published. Reordering that producer would break the guarantee silently.
export const CONVERSATION_DERIVED_ESCALATION_SOURCE = [
  'agent_crisis_flag',
  'transcript_crisis_scan',
] as const satisfies readonly EscalationSource[];
export type ConversationDerivedEscalationSource =
  (typeof CONVERSATION_DERIVED_ESCALATION_SOURCE)[number];

// ─── active-escalation source (member-detail wire) ──────────────────────────
//
// The source vocabulary the member-detail response carries. Deliberately its
// own two-value set rather than ESCALATION_SOURCE: that response is parsed at
// runtime by a client bundle the server cannot upgrade, so a value the bundle
// does not know fails the whole member read (ADR-135 Rule 3). Widening the
// server vocabulary must therefore never widen this one.
//
// On this wire the value answers one question — where is the evidence — and
// there are only two answers: the assessment band, or the conversation
// transcript. `agent_crisis_flag` is the transcript answer, which is why a
// scan-sourced escalation is carried as it.
export const ACTIVE_ESCALATION_SOURCE = [
  'assessment_suicide_screen_positive',
  'agent_crisis_flag',
] as const;
export const activeEscalationSourceSchema = z.enum(ACTIVE_ESCALATION_SOURCE);
export type ActiveEscalationSource = z.infer<typeof activeEscalationSourceSchema>;

/**
 * The one bridge from the stored source to the member-detail wire.
 *
 * A switch with no default on purpose: a new stored source is a compile error
 * here, so it cannot reach the wire by falling through.
 */
export function toActiveEscalationSource(source: EscalationSource): ActiveEscalationSource {
  switch (source) {
    // The withdrawal check has no transcript either: its evidence sits on the
    // intake panel, which is the answer this value gives. The response's
    // optional `intakeWithdrawalCheck` flag lets a current bundle name it.
    case 'assessment_suicide_screen_positive':
    case 'intake_withdrawal_screen_positive':
      return 'assessment_suicide_screen_positive';
    case 'agent_crisis_flag':
    case 'transcript_crisis_scan':
      return 'agent_crisis_flag';
  }
}

// ─── escalation status ──────────────────────────────────────────────────────
//
// The review lifecycle. Two values, and deliberately only two: an escalation
// has either been looked at by the care team or it has not.
//
// "In progress" is NOT a third value, although staff see it. It is derived:
// an open escalation that someone has claimed (`claimed_by_internal_user_id`
// set). A claim says who is on it, not how far the review has got, and keeping
// it out of `status` is what lets Mark handled stay one transition from open
// that anyone may make, claimed or not. A status value would also make every
// reader that branches on `open` silently miss claimed escalations.
//
// The table shipped without this column on exactly that reasoning — no
// consumer transitioned a lifecycle, so it would have encoded a constant. It
// exists now because the member-detail surface reads it to decide whether to
// raise the banner, and the mark-handled write sets it.
//
// `severity` is still absent, for the unchanged reason: the Gamma scope calls
// for clinically-named tiers and clinical has not delivered the vocabulary, so
// a single-valued enum would encode precision the data does not carry.
export const ESCALATION_STATUS = ['open', 'handled'] as const;
export const escalationStatusSchema = z.enum(ESCALATION_STATUS);
export type EscalationStatus = z.infer<typeof escalationStatusSchema>;

// ─── escalation event kind ──────────────────────────────────────────────────
//
// What one row of the append-only escalation event log records — together, the
// escalation's history:
//
//   - `claimed`, `released`, `taken_over`: the claim lifecycle.
//   - `raised`: the escalation was created. The one kind with no staff actor,
//     because a producer raises it, not a person.
//   - `contact_attempt`: an unsuccessful attempt to reach the member. The
//     attempt itself stays in `escalation_contact_attempts`; this row points at
//     it and carries no copy of its note.
//   - `commented`: a staff comment, the row's note.
//   - `handled`: the mark-handled transition, by the resolver.
//   - `released_by_manager`, `assigned`: an escalation manager releasing a
//     claim someone else holds, or making a named person the holder.
//
// APPENDED, never inserted or reordered: the tuple feeds the `kind` CHECK, and
// rows of every shipped kind are immutable. A reader that meets a kind it does
// not know renders it generically rather than failing.
export const ESCALATION_EVENT_KIND = [
  'claimed',
  'released',
  'taken_over',
  'raised',
  'contact_attempt',
  'commented',
  'handled',
  'released_by_manager',
  'assigned',
] as const;
export const escalationEventKindSchema = z.enum(ESCALATION_EVENT_KIND);
export type EscalationEventKind = z.infer<typeof escalationEventKindSchema>;

/**
 * The longest note any event other than a comment may carry, after trimming:
 * the optional reason on a release or a take-over. Here rather than beside the
 * request schemas because the event table's CHECK reads it too. The same bound
 * as a contact attempt's note: a working note, not a shadow clinical record.
 */
export const ESCALATION_EVENT_NOTE_MAX_LENGTH = 500;

/**
 * The longest comment, after trimming. Longer than a reason, because a comment
 * is how one shift hands context to the next; shorter than the handling notes,
 * which close the escalation. The event table's CHECK reads it.
 */
export const ESCALATION_COMMENT_MAX_LENGTH = 2000;

// ─── escalation disposition ─────────────────────────────────────────────────
//
// What the care team did about it — the outcome vocabulary, taken verbatim from
// the prototype's dropdown rather than invented here.
//
// `referred_988` names the US national suicide and crisis lifeline. It is a
// concrete referral action, not a clinical category, which is why it belongs in
// this vocabulary while the `escalations.category` column stays empty.
//
// `false_positive` records that the signal did not reflect a real safety
// concern. It is a judgement about the SIGNAL, never about the member, and it
// closes the escalation without implying the conversation or assessment behind
// it was wrong to surface.
//
// `brief_safety_assessment_completed` is NIMH's follow-up to a positive ASQ
// screen, so it only makes sense when the concern was suicidal ideation (it is
// offered for any non-acute screen there, negative included).
// `emergency_evaluation_initiated` is the acute-positive path: NIMH calls for an
// immediate full evaluation. Which value may close which escalation is decided
// by `allowedEscalationResolutions` in `handling-decision.ts`, not here — this
// list is only the vocabulary. The UI calls it "Resolution reason"; the column
// keeps its original name because renaming a populated column is an
// expand/contract sequence that buys no behaviour.
//
// Written only by the mark-handled path; declared here so the read and write
// halves of this surface share one migration instead of needing an
// expand/contract sequence between them.
export const ESCALATION_DISPOSITION = [
  'contacted_member_safe',
  'brief_safety_assessment_completed',
  'emergency_evaluation_initiated',
  'referred_988',
  'false_positive',
  'other',
] as const;
export const escalationDispositionSchema = z.enum(ESCALATION_DISPOSITION);
export type EscalationDisposition = z.infer<typeof escalationDispositionSchema>;

// ─── handling concern ───────────────────────────────────────────────────────
//
// What the clinician judged the concern to be, after reviewing the evidence.
// The four values match the categories the agent's `escalate_crisis` tool is
// prompted to fire on.
//
// Deliberately NOT stored in `escalations.category`. That column is reserved
// for a clinically-owned classification of the SIGNAL, which no producer can
// make today; this is the care team's judgement after review, a different fact
// with a different author. Folding the two together would make a signal look
// classified when only a human had classified it.
export const ESCALATION_HANDLING_CONCERN = [
  'suicidal_ideation',
  'self_harm',
  'harm_to_others',
  'medical_risk',
] as const;
export const escalationHandlingConcernSchema = z.enum(ESCALATION_HANDLING_CONCERN);
export type EscalationHandlingConcern = z.infer<typeof escalationHandlingConcernSchema>;

// ─── ASQ result ─────────────────────────────────────────────────────────────
//
// The NIMH Ask Suicide-Screening Questions result. Always COMPUTED from the
// screen by `computeAsqResult` and never entered by a clinician, so the rule
// cannot drift between people or between Atlas and the API.
//
// `declined` is an adult's refusal of the screen, which NIMH says is NOT a
// positive screen (it is documented, and nothing follows from it alone). A
// youth's refusal is a non-acute positive instead, so `declined` only ever
// describes an adult.
export const ESCALATION_ASQ_RESULT = [
  'negative',
  'non_acute_positive',
  'acute_positive',
  'declined',
] as const;
export const escalationAsqResultSchema = z.enum(ESCALATION_ASQ_RESULT);
export type EscalationAsqResult = z.infer<typeof escalationAsqResultSchema>;

// ─── ASQ attempt recency ────────────────────────────────────────────────────
//
// The NIMH follow-up to a Yes on Q4 ("Have you ever tried to kill yourself?"):
// when the most recent attempt was. It matters to the guidance — a screen that
// is positive only because of an attempt over a year ago is the NIMH exception
// to the brief suicide safety assessment for an adult.
export const ESCALATION_ASQ_ATTEMPT_RECENCY = ['within_12_months', 'over_1_year'] as const;
export const escalationAsqAttemptRecencySchema = z.enum(ESCALATION_ASQ_ATTEMPT_RECENCY);
export type EscalationAsqAttemptRecency = z.infer<typeof escalationAsqAttemptRecencySchema>;

// ─── mark-handled outcome ───────────────────────────────────────────────────
//
// What the mark-handled write actually did. Two values, and the second is the
// reason this vocabulary exists at all: marking an escalation handled is
// idempotent, so a call that changed nothing is a legitimate result rather
// than an error, and the caller has to be able to tell the two apart.
//
// `already_handled` is deliberately NOT an error code. A clinician
// double-clicking submit, or two clinicians reaching the same alert, is the
// expected steady state on a safety surface — answering `409` would render a
// harmless double-click as a failure to someone mid-crisis. It IS surfaced to
// the clinician, though: their chosen disposition was not the one recorded,
// and a UI that renders this outcome as a plain success would let them believe
// otherwise.
//
// The shape follows HEALTHIE_LINK_OUTCOME, where a `skipped_*` result is
// likewise a 200 the care team acts on rather than a failed request.
export const ESCALATION_MARK_HANDLED_OUTCOME = ['handled', 'already_handled'] as const;
export const escalationMarkHandledOutcomeSchema = z.enum(ESCALATION_MARK_HANDLED_OUTCOME);
export type EscalationMarkHandledOutcome = z.infer<typeof escalationMarkHandledOutcomeSchema>;

// ─── contact-attempt outcome ────────────────────────────────────────────────
//
// What logging an unsuccessful contact attempt actually did. `logged` appended
// a row and left the escalation open. `already_handled` wrote nothing: an
// attempt is never filed against an escalation that is already resolved, and
// the caller is told so rather than seeing a silent success.
//
// `already_handled` is a 200, not a 409, for the same reason as on the
// mark-handled outcome, so a client handles both escalation writes alike: the
// re-read projection shows the escalation closed, and the outcome says why
// nothing was written. Unlike mark-handled, where someone else's handle
// landed, here nothing landed at all.
export const ESCALATION_CONTACT_ATTEMPT_OUTCOME = ['logged', 'already_handled'] as const;
export const escalationContactAttemptOutcomeSchema = z.enum(ESCALATION_CONTACT_ATTEMPT_OUTCOME);
export type EscalationContactAttemptOutcome = z.infer<typeof escalationContactAttemptOutcomeSchema>;

// ─── contact-attempt list bound ─────────────────────────────────────────────
//
// How many of an escalation's attempts the member-detail projection lists,
// newest first. The count beside the list is always the true total, so a
// long history is summarised rather than shipped whole on every member read.
// One number so the repo's LIMIT and the wire schema's `.max()` cannot drift.
export const ESCALATION_CONTACT_ATTEMPTS_LIST_LIMIT = 20;
